import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KPI, SeverityBadge, StatusBadge } from "@/components/Primitives";
import {
  AlertTriangle,
  ShieldAlert,
  Ticket,
  Server,
  Repeat,
  FileSignature,
  HardDriveDownload,
  ArrowUpRight,
  Sparkles,
  Activity,
  Building2,
  RefreshCw,
} from "lucide-react";

export default function Dashboard() {
  const [data, setData] = useState({
    executive: null,
    clients: [],
    tickets: [],
    agents: [],
    assets: [],
    recurring: [],
    contracts: [],
    licenses: [],
    backups: [],
    security: [],
    compliance: [],
  });

  const [drill, setDrill] = useState("critical_tickets");
  const [aiBrief, setAiBrief] = useState("");
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const generateExecutiveSummary = async () => {
    setAiOpen(true);
    setAiLoading(true);
    setAiBrief("");

    try {
      const { data } = await api.post("/ai/exec-summary");
      setAiBrief(data.summary || data.text || data.brief || JSON.stringify(data, null, 2));
    } catch (err) {
      console.error("AI executive summary failed:", err);
      toast.error(err?.response?.data?.detail || err?.message || "AI Executive Summary failed");
      setAiBrief("AI Executive Summary could not be generated. Check backend AI endpoint and API key configuration.");
    } finally {
      setAiLoading(false);
    }
  };

  const load = async () => {
    const calls = await Promise.allSettled([
      api.get("/dashboard/executive"),
      api.get("/clients"),
      api.get("/tickets", { params: { limit: 1000 } }),
      api.get("/agents"),
      api.get("/assets", { params: { limit: 2000 } }),
      api.get("/recurring-issues"),
      api.get("/contracts"),
      api.get("/licenses"),
      api.get("/backups"),
      api.get("/security/findings"),
      api.get("/compliance/findings"),
    ]);

    setData({
      executive: calls[0].status === "fulfilled" ? calls[0].value.data : null,
      clients: calls[1].status === "fulfilled" ? calls[1].value.data : [],
      tickets: calls[2].status === "fulfilled" ? calls[2].value.data : [],
      agents: calls[3].status === "fulfilled" ? calls[3].value.data : [],
      assets: calls[4].status === "fulfilled" ? calls[4].value.data : [],
      recurring: calls[5].status === "fulfilled" ? calls[5].value.data : [],
      contracts: calls[6].status === "fulfilled" ? calls[6].value.data : [],
      licenses: calls[7].status === "fulfilled" ? calls[7].value.data : [],
      backups: calls[8].status === "fulfilled" ? calls[8].value.data : [],
      security: calls[9].status === "fulfilled" ? calls[9].value.data : [],
      compliance: calls[10].status === "fulfilled" ? calls[10].value.data : [],
    });
  };

  useEffect(() => { load(); }, []);

  const clientMap = useMemo(() => Object.fromEntries(data.clients.map(c => [c.id, c])), [data.clients]);

  const openTickets = data.tickets.filter(t => t.status !== "closed");
  const criticalTickets = openTickets.filter(t => t.priority === "critical");
  const overdueTickets = openTickets.filter(t => t.sla_deadline && new Date(t.sla_deadline) < new Date());

  const offlineAgents = data.agents.filter(a => a.status !== "online");
  const staleAgents = data.agents.filter(a => {
    if (!a.last_heartbeat) return true;
    return Date.now() - new Date(a.last_heartbeat).getTime() > 5 * 60 * 1000;
  });

  const nonCompliantAssets = data.assets.filter(a =>
    a.compliance_status === "non_compliant" ||
    a.status === "at_risk" ||
    a.security_status === "at_risk"
  );

  const needsUpdates = data.assets.filter(a =>
    a.patch_status === "missing_updates" ||
    a.needs_updates ||
    a.os_patch_age_days > 30
  );

  const failedBackups = data.backups.filter(b =>
    b.status === "failed" ||
    b.last_status === "failed" ||
    b.health === "failed"
  );

  const expiringContracts = data.contracts.filter(c => {
    const d = c.end_date || c.expiry_date || c.expires_at;
    if (!d) return false;
    const days = (new Date(d).getTime() - Date.now()) / 86400000;
    return days >= 0 && days <= 90;
  });

  const expiringLicenses = data.licenses.filter(l => {
    const d = l.expiry_date || l.expires_at || l.renewal_date;
    if (!d) return false;
    const days = (new Date(d).getTime() - Date.now()) / 86400000;
    return days >= 0 && days <= 90;
  });

  const securityFindings = data.security.filter(s => s.status !== "closed" && s.status !== "resolved");
  const complianceFindings = data.compliance.filter(c => c.status !== "closed" && c.status !== "resolved");

  const clientRows = data.clients.map(c => {
    const cid = c.id;
    const ctickets = openTickets.filter(t => t.client_id === cid);
    const ccritical = ctickets.filter(t => t.priority === "critical");
    const cassets = data.assets.filter(a => a.client_id === cid);
    const cnon = cassets.filter(a => a.compliance_status === "non_compliant" || a.status === "at_risk" || a.security_status === "at_risk");
    const cagents = data.agents.filter(a => a.client_id === cid || cassets.some(asset => asset.id === a.asset_id));
    const coffline = cagents.filter(a => a.status !== "online");

    const risk =
      ccritical.length * 12 +
      coffline.length * 5 +
      cnon.length * 4 +
      Math.max(0, ctickets.length - 10);

    return {
      ...c,
      open_tickets: ctickets.length,
      critical_tickets: ccritical.length,
      non_compliant: cnon.length,
      offline_agents: coffline.length,
      risk,
    };
  }).sort((a, b) => b.risk - a.risk);

  const drillData = {
    critical_tickets: criticalTickets,
    open_tickets: openTickets,
    sla_breaches: overdueTickets,
    offline_agents: offlineAgents,
    stale_agents: staleAgents,
    non_compliant: nonCompliantAssets,
    needs_updates: needsUpdates,
    security_findings: securityFindings,
    compliance_findings: complianceFindings,
    recurring: data.recurring,
    failed_backups: failedBackups,
    expiring_contracts: expiringContracts,
    expiring_licenses: expiringLicenses,
    clients_at_risk: clientRows.filter(c => c.risk > 0),
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Executive Command Center</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">What needs attention today</h1>
          <p className="text-sm text-zinc-500 mt-1">Drill down into risk, tickets, compliance, endpoint health, and business exposure.</p>
        </div>
        <button
          onClick={generateExecutiveSummary}
          disabled={aiLoading}
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-800 disabled:text-zinc-500 text-white rounded-md px-4 py-2 text-sm flex items-center gap-2"
        >
          <Sparkles size={15} /> {aiLoading ? "Generating..." : "AI Executive Summary"}
        </button>
      </div>

      {aiOpen && (
        <section className="bg-zinc-950 border border-blue-900/50 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
            <div className="font-display font-medium flex items-center gap-2">
              <Sparkles size={15} className="text-blue-400" />
              AI Executive Brief
            </div>
            <button onClick={() => setAiOpen(false)} className="text-xs text-zinc-500 hover:text-zinc-200">Close</button>
          </div>
          <pre className="p-5 text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed max-h-96 overflow-auto">
            {aiLoading ? "Generating CIO-grade operational summary..." : aiBrief}
          </pre>
        </section>
      )}

      <section className="grid grid-cols-2 xl:grid-cols-6 gap-4">
        <ActionCard icon={Ticket} label="Critical Tickets" value={criticalTickets.length} tone="bad" active={drill === "critical_tickets"} onClick={() => setDrill("critical_tickets")} />
        <ActionCard icon={AlertTriangle} label="SLA Breaches" value={overdueTickets.length} tone="bad" active={drill === "sla_breaches"} onClick={() => setDrill("sla_breaches")} />
        <ActionCard icon={Server} label="Offline Agents" value={offlineAgents.length} tone="bad" active={drill === "offline_agents"} onClick={() => setDrill("offline_agents")} />
        <ActionCard icon={RefreshCw} label="Needs Updates" value={needsUpdates.length} tone="warn" active={drill === "needs_updates"} onClick={() => setDrill("needs_updates")} />
        <ActionCard icon={ShieldAlert} label="Security Findings" value={securityFindings.length} tone="warn" active={drill === "security_findings"} onClick={() => setDrill("security_findings")} />
        <ActionCard icon={HardDriveDownload} label="Failed Backups" value={failedBackups.length} tone="bad" active={drill === "failed_backups"} onClick={() => setDrill("failed_backups")} />
      </section>

      <section className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        <KPI label="Clients" value={data.clients.length || data.executive?.clients || 0} sub="Active engagements" />
        <KPI label="Total Assets" value={data.assets.length || data.executive?.total_assets || 0} sub={`${data.agents.filter(a => a.status === "online").length} agents online`} tone="good" />
        <KPI label="Open Tickets" value={openTickets.length || data.executive?.open_tickets || 0} sub={`${criticalTickets.length} critical`} tone={criticalTickets.length ? "bad" : "default"} />
        <KPI label="Non-Compliant" value={nonCompliantAssets.length + complianceFindings.length} sub="Assets + findings" tone="warn" />
        <KPI label="Recurring Issues" value={data.recurring.length || data.executive?.recurring_issues || 0} sub="Detected" tone="warn" />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Panel title="Client Risk Leaderboard" className="xl:col-span-2">
          <ClientRiskTable rows={clientRows.slice(0, 10)} />
        </Panel>

        <Panel title="Business Risk">
          <RiskButton label="Contracts expiring 90d" value={expiringContracts.length} active={drill === "expiring_contracts"} onClick={() => setDrill("expiring_contracts")} />
          <RiskButton label="Licenses expiring 90d" value={expiringLicenses.length} active={drill === "expiring_licenses"} onClick={() => setDrill("expiring_licenses")} />
          <RiskButton label="Clients at risk" value={clientRows.filter(c => c.risk > 0).length} active={drill === "clients_at_risk"} onClick={() => setDrill("clients_at_risk")} />
          <RiskButton label="Recurring issues" value={data.recurring.length} active={drill === "recurring"} onClick={() => setDrill("recurring")} />
        </Panel>
      </section>

      <section className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <MiniDrill icon={Ticket} label="Open Tickets" value={openTickets.length} active={drill === "open_tickets"} onClick={() => setDrill("open_tickets")} />
        <MiniDrill icon={Activity} label="Stale Agents" value={staleAgents.length} active={drill === "stale_agents"} onClick={() => setDrill("stale_agents")} />
        <MiniDrill icon={ShieldAlert} label="Compliance Findings" value={complianceFindings.length} active={drill === "compliance_findings"} onClick={() => setDrill("compliance_findings")} />
        <MiniDrill icon={Repeat} label="Recurring Issues" value={data.recurring.length} active={drill === "recurring"} onClick={() => setDrill("recurring")} />
      </section>

      <Panel title={`Drilldown · ${labelFor(drill)} (${drillData[drill]?.length || 0})`}>
        <DrilldownTable type={drill} rows={drillData[drill] || []} clients={clientMap} />
      </Panel>
    </div>
  );
}

function ActionCard({ icon: Icon, label, value, tone, active, onClick }) {
  const color = tone === "bad" ? "text-red-300" : tone === "warn" ? "text-amber-300" : "text-emerald-300";
  return (
    <button onClick={onClick} className={`bg-zinc-950 border rounded-md p-4 text-left hover:border-blue-700 transition-colors ${active ? "border-blue-600" : "border-zinc-900"}`}>
      <div className="flex items-center justify-between">
        <Icon size={16} className={color} />
        <ArrowUpRight size={13} className="text-zinc-600" />
      </div>
      <div className={`font-mono text-3xl mt-4 ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{label}</div>
    </button>
  );
}

function MiniDrill({ icon: Icon, label, value, active, onClick }) {
  return (
    <button onClick={onClick} className={`bg-zinc-950 border rounded-md p-4 text-left hover:border-blue-700 ${active ? "border-blue-600" : "border-zinc-900"}`}>
      <Icon size={15} className="text-blue-400" />
      <div className="font-mono text-2xl mt-3">{value}</div>
      <div className="text-xs text-zinc-500">{label}</div>
    </button>
  );
}

function Panel({ title, children, className = "" }) {
  return (
    <div className={`bg-zinc-950 border border-zinc-900 rounded-md overflow-hidden ${className}`}>
      <div className="px-5 py-3 border-b border-zinc-900 font-display font-medium">{title}</div>
      {children}
    </div>
  );
}

function RiskButton({ label, value, active, onClick }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center justify-between px-5 py-3 border-b border-zinc-900 hover:bg-zinc-900/40 ${active ? "bg-blue-950/20" : ""}`}>
      <span className="text-sm text-zinc-400">{label}</span>
      <span className="font-mono text-lg">{value}</span>
    </button>
  );
}

function ClientRiskTable({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr>
          <th className="text-left px-4 py-3">Client</th>
          <th className="text-left px-4 py-3">Health</th>
          <th className="text-left px-4 py-3">Open</th>
          <th className="text-left px-4 py-3">Critical</th>
          <th className="text-left px-4 py-3">Offline</th>
          <th className="text-left px-4 py-3">Non-Compliant</th>
          <th className="text-left px-4 py-3">Risk</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(c => (
          <tr key={c.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
            <td className="px-4 py-2"><Link to={`/clients/${c.id}`} className="hover:text-blue-400 font-medium">{c.name}</Link></td>
            <td className="px-4 py-2 font-mono">{c.health_score || 0}%</td>
            <td className="px-4 py-2 font-mono">{c.open_tickets}</td>
            <td className="px-4 py-2 font-mono text-red-300">{c.critical_tickets}</td>
            <td className="px-4 py-2 font-mono text-amber-300">{c.offline_agents}</td>
            <td className="px-4 py-2 font-mono">{c.non_compliant}</td>
            <td className="px-4 py-2 font-mono">{c.risk}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DrilldownTable({ type, rows, clients }) {
  if (type.includes("ticket")) return <TicketRows rows={rows} clients={clients} />;
  if (type.includes("agent")) return <AgentRows rows={rows} />;
  if (["non_compliant", "needs_updates"].includes(type)) return <AssetRows rows={rows} />;
  if (type.includes("security") || type.includes("compliance")) return <FindingRows rows={rows} clients={clients} />;
  if (type.includes("contract")) return <GenericRows rows={rows} fields={["name", "client_id", "end_date", "status"]} clients={clients} />;
  if (type.includes("license")) return <GenericRows rows={rows} fields={["name", "product", "expiry_date", "status"]} clients={clients} />;
  if (type.includes("backup")) return <GenericRows rows={rows} fields={["name", "client_id", "status", "last_run"]} clients={clients} />;
  if (type === "clients_at_risk") return <ClientRiskTable rows={rows} />;
  return <GenericRows rows={rows} fields={["title", "client_id", "severity", "status"]} clients={clients} />;
}

function TicketRows({ rows, clients }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr><th className="text-left px-4 py-3">#</th><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Priority</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">SLA</th></tr>
      </thead>
      <tbody>
        {rows.map(t => (
          <tr key={t.id} className="border-b border-zinc-900">
            <td className="px-4 py-2 font-mono text-zinc-500">#{t.number}</td>
            <td className="px-4 py-2"><Link to={`/tickets/${t.id}`} className="hover:text-blue-400">{t.title}</Link></td>
            <td className="px-4 py-2 text-zinc-400">{clients[t.client_id]?.name || t.client_id || "—"}</td>
            <td className="px-4 py-2"><SeverityBadge level={t.priority} /></td>
            <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
            <td className="px-4 py-2 font-mono text-xs text-zinc-500">{t.sla_deadline?.slice(0,16)?.replace("T"," ")}</td>
          </tr>
        ))}
        {rows.length === 0 && <Empty colSpan={6} />}
      </tbody>
    </table>
  );
}

function AgentRows({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr><th className="text-left px-4 py-3">Hostname</th><th className="text-left px-4 py-3">User</th><th className="text-left px-4 py-3">OS</th><th className="text-left px-4 py-3">IP</th><th className="text-left px-4 py-3">Last HB</th><th className="text-left px-4 py-3">Status</th></tr>
      </thead>
      <tbody>
        {rows.map(a => (
          <tr key={a.id} className="border-b border-zinc-900">
            <td className="px-4 py-2 font-mono">{a.hostname || a.id}</td>
            <td className="px-4 py-2 text-zinc-400">{a.metrics?.current_user || "—"}</td>
            <td className="px-4 py-2 text-zinc-400">{a.os}</td>
            <td className="px-4 py-2 font-mono text-xs">{a.metrics?.ip_address || "—"}</td>
            <td className="px-4 py-2 font-mono text-xs text-zinc-500">{a.last_heartbeat?.slice(0,19)?.replace("T"," ") || "—"}</td>
            <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
          </tr>
        ))}
        {rows.length === 0 && <Empty colSpan={6} />}
      </tbody>
    </table>
  );
}

function AssetRows({ rows }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr><th className="text-left px-4 py-3">Asset</th><th className="text-left px-4 py-3">OS</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Compliance</th><th className="text-left px-4 py-3">Patch</th></tr>
      </thead>
      <tbody>
        {rows.map(a => (
          <tr key={a.id} className="border-b border-zinc-900">
            <td className="px-4 py-2"><Link to={`/assets/${a.id}`} className="font-mono hover:text-blue-400">{a.hostname || a.name || a.id}</Link></td>
            <td className="px-4 py-2 text-zinc-400">{a.os || "—"}</td>
            <td className="px-4 py-2"><StatusBadge status={a.status || "active"} /></td>
            <td className="px-4 py-2 text-zinc-400">{a.compliance_status || "unknown"}</td>
            <td className="px-4 py-2 text-zinc-400">{a.patch_status || (a.needs_updates ? "needs_updates" : "unknown")}</td>
          </tr>
        ))}
        {rows.length === 0 && <Empty colSpan={5} />}
      </tbody>
    </table>
  );
}

function FindingRows({ rows, clients }) {
  return <GenericRows rows={rows} fields={["title", "client_id", "severity", "status"]} clients={clients} />;
}

function GenericRows({ rows, fields, clients }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-zinc-900/50 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <tr>{fields.map(f => <th key={f} className="text-left px-4 py-3">{f.replaceAll("_", " ")}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id || i} className="border-b border-zinc-900">
            {fields.map(f => (
              <td key={f} className="px-4 py-2 text-zinc-400">
                {f === "client_id" ? clients[r[f]]?.name || r[f] || "—" : String(r[f] ?? "—").slice(0, 120)}
              </td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && <Empty colSpan={fields.length} />}
      </tbody>
    </table>
  );
}

function Empty({ colSpan }) {
  return <tr><td colSpan={colSpan} className="px-4 py-8 text-sm text-zinc-500">No records found.</td></tr>;
}

function labelFor(k) {
  return {
    critical_tickets: "Critical Tickets",
    open_tickets: "Open Tickets",
    sla_breaches: "SLA Breaches",
    offline_agents: "Offline Agents",
    stale_agents: "Stale Agents",
    non_compliant: "Non-Compliant Assets",
    needs_updates: "Devices Needing Updates",
    security_findings: "Security Findings",
    compliance_findings: "Compliance Findings",
    recurring: "Recurring Issues",
    failed_backups: "Failed Backups",
    expiring_contracts: "Expiring Contracts",
    expiring_licenses: "Expiring Licenses",
    clients_at_risk: "Clients at Risk",
  }[k] || k;
}
